/**
 * ProjectWorkflowPage — settings page for editing a project's
 * stage gates (project-tier overrides on top of workspace defaults).
 *
 * Step 6 of the workflow rollout. Counterpart to the Section
 * Templates settings page (b869328): same shape, different editor.
 *
 * Mounted at /v2/projects/:id/workflow. Linked from the project
 * detail page's actions bar next to "Configure sections".
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, GitBranch } from "lucide-react";
import { v2Api } from "@/lib/v2Api";
import { Button } from "@/components/ui/button";
import { StageGatesEditor } from "@/pages/v2/StageGatesPage";

export default function ProjectWorkflowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);

  useEffect(() => {
    if (!id) return;
    v2Api.projects.get(id).then(setProject).catch(() => {});
  }, [id]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="px-6 py-4 border-b bg-white sticky top-0 z-10">
        <button
          onClick={() => navigate(`/v2/projects/${id}`)}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1"
        >
          <ArrowLeft className="w-3 h-3" /> Back to project
        </button>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-blue-600" />
          Workflow gates
          {project && (
            <span className="text-sm text-slate-500 font-normal">
              · {project.name}
            </span>
          )}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Project-tier overrides for stage artifact gates. A row here wins
          over the workspace default for the same target stage. Use the
          "Override" button on a workspace-default row to start a per-project
          customisation; "Add gate" creates a brand-new project-tier rule.
        </p>
      </div>
      <div className="p-6 max-w-5xl mx-auto space-y-4">
        <StageGatesEditor projectId={id} />
        <div className="flex justify-end">
          <Link to={`/v2/projects/${id}`}>
            <Button variant="outline">Done</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
